import { Link } from "@/lib/router-compat";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import type { IndustryHowWeHelpCard } from "@/data/industriesData";
import { useLanguage } from "@/hooks/useLanguage";
import { localizeHref } from "@/lib/localizeHref";
import { industryUI } from "@/i18n/industryDetail";

interface Props {
  cards: IndustryHowWeHelpCard[];
  color?: string;
  /** Question-shaped H2 override (GEO) */
  sectionTitle?: string;
}

/** Map service page paths to their brand colors */
const SERVICE_COLORS: Record<string, string> = {
  "/en/services/regulatory-affairs": "#238472",
  "/en/services/quality-assurance": "#BC8CE7",
  "/en/services/clinical-solutions": "#FFC60C",
  "/en/services/qualification-validation": "#63A9FF",
  "/en/services/lab-analytical-services": "#5C43DE",
  "/en/services/pharmacovigilance": "#FB5656",
  "/en/services/software-digital": "#4AC7DB",
  "/en/services/regulatory-toxicology": "#C6E04B",
  "/en/services/regulatory-toxicology/biocompatibility-biological-evaluation": "#C6E04B",
};

const getServiceColor = (href?: string, fallback?: string) =>
  (href && SERVICE_COLORS[href]) || fallback || "var(--primary)";

const IndustryHowWeHelp = ({ cards, color, sectionTitle }: Props) => {
  const lang = useLanguage();
  const ui = industryUI[lang] ?? industryUI.en;
  if (cards.length === 0) return null;

  return (
    <section className="py-12 sm:py-16 scroll-mt-20">
      <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
        {/* Header row */}
        <div className="flex items-center gap-3 mb-2">
          <span className="w-6 h-0.5 rounded-full bg-primary" />
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{({ en: "Life Sciences Operations", es: "Operaciones de Life Sciences", "zh-cn": "生命科学运营支持" }[lang])}</p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-2 mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-foreground">
            {sectionTitle ?? ui.howWeHelp}
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              {cards.length}
            </span>
          </h2>
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-4xl mx-auto">
          {cards.map((card, i) => {
            const sc = getServiceColor(card.href, color);
            return (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.25, delay: i * 0.03 }}
              >
                <div className="group rounded-lg border border-border/50 overflow-hidden hover:shadow-sm transition-all h-full flex flex-col" style={{ borderTopColor: sc, borderTopWidth: 2 }}>
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="text-base font-semibold text-foreground group-hover:text-primary transition-colors mb-3">
                      {card.title}
                    </h3>
                    <ul className="space-y-2 mb-4 flex-1">
                      {card.bullets.map((b, j) => (
                        <li key={j} className="text-sm text-muted-foreground leading-relaxed flex items-start gap-2.5">
                          <span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: sc }} />
                          {b}
                        </li>
                      ))}
                    </ul>
                    {card.href && (
                      <Link
                        to={localizeHref(card.href, lang)}
                        className="text-sm font-medium flex items-center gap-1.5 hover:gap-2.5 transition-all"
                        style={{ color: sc }}
                      >
                        {ui.discoverMore} <ArrowRight size={14} />
                      </Link>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default IndustryHowWeHelp;
